import { Injectable } from '@jack-henry/frontend-utils/di';
import {
    HttpTransportType,
    HubConnection,
    HubConnectionBuilder,
    LogLevel,
} from '@microsoft/signalr';
import { ConfigurationService } from '../config';
import { AUTH_TOKEN, TmHttpClient } from '../http';
import { RealtimeService } from './realtime.service';

/**
 * A service for interacting with live messages and events from the TM API in realtime.
 * Compatible with ASP.net core SignalR implementation.
 */
@Injectable()
export class ModernRealtimeService extends RealtimeService {
    constructor(
        config: ConfigurationService,
        private http: TmHttpClient
    ) {
        super(config);
        this.connected$.subscribe(isConnected => {
            this.isConnected = isConnected;
        });
    }

    private connection?: HubConnection;

    public async init(useLogging = false) {
        if (this.connection) {
            return;
        }

        this.connection = this.createConnection(useLogging);
        this.connection.onclose(() => {
            this.connectionSource.emit(false);
        });
        await this.connect();
    }

    public async disconnect(): Promise<void> {
        if (!this.connection) {
            return;
        }

        await this.connection.stop();
        this.connection = undefined;
    }

    /**
     * Subscribe to a `topic` and open a connection, if one hasn't already been established.
     */
    public async subscribe<T>(topic: string, callback: (message: T) => void) {
        if (!this.connection) {
            throw RealtimeService.createInitError(`Could not stop subscribe to topic "${topic}"`);
        }

        this.connection.on(topic, callback);
        this.topics.push(topic);

        return {
            unsubscribe: () => {
                this.unsubscribe(topic, callback);
            },
        };
    }

    public publish<T extends unknown[]>(topic: string, ...args: T): Promise<void> {
        if (!this.connection) {
            throw RealtimeService.createInitError(`Could not publish to topic "${topic}"`);
        }

        return this.connection.invoke(topic, ...args);
    }

    private async connect() {
        if (!this.connection) {
            throw RealtimeService.createInitError('Could not connect');
        }

        this.connection.onreconnected(() => {
            this.connectionSource.emit(true);
        });

        this.connection.onclose(() => {
            this.connectionSource.emit(false);
        });

        try {
            await this.connection.start();
            this.connectionSource.emit(true);
        } catch (error) {
            this.connectionSource.emit(false);
            throw error;
        }
    }

    private unsubscribe<T>(topic: string, callback: (message: T) => void) {
        if (!this.connection) {
            throw RealtimeService.createInitError(`Could not unsubscribe from topic "${topic}"`);
        }

        this.connection.off(topic, callback);
        this.topics = this.topics.filter(t => t !== topic);
    }

    private createConnection(useLogging: boolean) {
        if (!this.http.authToken) {
            throw new Error('Auth token is required to establish a SignalR connection.');
        }

        return new HubConnectionBuilder()
            .withUrl(this.endpoint, {
                headers: {
                    [AUTH_TOKEN]: this.http.authToken,
                },
                withCredentials: true,
                transport: HttpTransportType.ServerSentEvents,
                skipNegotiation: false,
            })
            .configureLogging(useLogging ? LogLevel.Debug : LogLevel.None)
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: () => RealtimeService.RETRY_THRESHOLD,
            })
            .build();
    }
}
